import Layout from "hocs/layouts/Layout";
import Navbar from "components/navigation/Navbar";
import Footer from "components/navigation/Footer";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import DOMPurify from "dompurify";
import axios from "axios";
import { fetchJobDetail } from "utils/apiContent";

const onlyDigits = (value = "") => value.replace(/\D/g, "");
const allowedCvExtensions = [".pdf", ".doc", ".docx"];
const allowedCvMimeTypes = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const getAssetUrl = (path) => {
	if (!path) return "";
	if (String(path).startsWith("http")) return path;
	return `${process.env.REACT_APP_EXTERNAL_API_URL}/${String(path).replace(/^\/+/, "")}`;
};

const initialApplicationForm = {
	full_name: "",
	email: "",
	phone: "",
	cover_letter: "",
	cv: null,
	origin: "externo",
};
const APPLY_LIMIT_STORAGE_KEY = "jobs_public_apply_last_date";
const getTodayLocalDateKey = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};
const isApplyLockedTodayFromStorage = () => {
	try {
		return window.localStorage.getItem(APPLY_LIMIT_STORAGE_KEY) === getTodayLocalDateKey();
	} catch (error) {
		return false;
	}
};
const clampHtmlToPlainText = (html = "") =>
	String(DOMPurify.sanitize(String(html || ""), { ALLOWED_TAGS: [] }))
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim();

function JobDetail() {
	const params = useParams();
	const id = params.id;
	const [job, setJob] = useState(null);
	const [applicationForm, setApplicationForm] = useState(initialApplicationForm);
	const [isApplying, setIsApplying] = useState(false);
	const [applyFeedback, setApplyFeedback] = useState({ type: "", message: "" });
	const [applyLockedToday, setApplyLockedToday] = useState(false);
	const [showApplyForm, setShowApplyForm] = useState(false);

	useEffect(() => {
		let mounted = true;
		window.scrollTo(0, 0);
		const load = async () => {
			try {
				const data = await fetchJobDetail(id);
				if (mounted) {
					setJob(data);
				}
			} catch (error) {
				if (mounted) {
					setJob(null);
				}
			}
		};
		load();

		return () => {
			mounted = false;
		};
	}, [id]);

	useEffect(() => {
		setApplyLockedToday(isApplyLockedTodayFromStorage());
	}, [id]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setApplicationForm((prev) => ({
			...prev,
			[name]: name === "phone" ? onlyDigits(value).slice(0, 15) : value,
		}));
	};

	const handleCvChange = (event) => {
		const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
		setApplicationForm((prev) => ({ ...prev, cv: file }));
	};

	const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

	const isValidCvFile = (file) => {
		if (!file) return false;
		const lowerName = String(file.name || "").toLowerCase();
		const hasValidExtension = allowedCvExtensions.some((ext) => lowerName.endsWith(ext));
		const hasValidMime = !file.type || allowedCvMimeTypes.includes(file.type);
		return hasValidExtension && hasValidMime;
	};

	const handleApplySubmit = async (event) => {
		event.preventDefault();
		setApplyFeedback({ type: "", message: "" });

		const fullName = applicationForm.full_name.trim();
		const email = applicationForm.email.trim().toLowerCase();
		const phone = onlyDigits(applicationForm.phone).slice(0, 15);
		const coverLetter = applicationForm.cover_letter.trim();

		const lockedNow = applyLockedToday || isApplyLockedTodayFromStorage();
		if (lockedNow) {
			setApplyLockedToday(true);
			setApplyFeedback({
				type: "error",
				message: "Ya se realizó una postulación desde este computador hoy. Intenta mañana.",
			});
			return;
		}

		if (!job?.id) {
			setApplyFeedback({ type: "error", message: "No se pudo identificar la vacante." });
			return;
		}
		if (!fullName) {
			setApplyFeedback({ type: "error", message: "El nombre completo es obligatorio." });
			return;
		}
		if (!email || !isValidEmail(email)) {
			setApplyFeedback({ type: "error", message: "Ingresa un correo electrónico válido." });
			return;
		}
		if (!applicationForm.cv) {
			setApplyFeedback({ type: "error", message: "Adjunta tu hoja de vida en PDF o Word." });
			return;
		}
		if (!isValidCvFile(applicationForm.cv)) {
			setApplyFeedback({
				type: "error",
				message: "El archivo debe ser PDF, DOC o DOCX.",
			});
			return;
		}

		const formData = new FormData();
		formData.append("job", String(job.id));
		formData.append("full_name", fullName);
		formData.append("email", email);
		formData.append("cv", applicationForm.cv);
		formData.append("origin", applicationForm.origin || "web_public");
		if (phone) formData.append("phone", phone);
		if (coverLetter) formData.append("cover_letter", coverLetter);

		setIsApplying(true);
		try {
			await axios.post(
				`${process.env.REACT_APP_EXTERNAL_API_URL}/api/public/apply/`,
				formData
			);
			setApplyFeedback({
				type: "success",
				message: "Tu postulación fue enviada correctamente.",
			});
			setApplicationForm(initialApplicationForm);
			setApplyLockedToday(true);
			setShowApplyForm(false);
			try {
				window.localStorage.setItem(APPLY_LIMIT_STORAGE_KEY, getTodayLocalDateKey());
			} catch (storageError) {
				// Si localStorage falla, no bloqueamos la UX.
			}
		} catch (error) {
			const serverMessage =
				error?.response?.data?.detail ||
				error?.response?.data?.error ||
				error?.response?.data?.message;
			setApplyFeedback({
				type: "error",
				message: serverMessage || "No fue posible enviar la postulación. Intenta de nuevo.",
			});
		} finally {
			setIsApplying(false);
		}
	};

	return (
		<Layout>
			<header className="flex justify-center">
				<Navbar />
			</header>
			{job && String(job.id) === String(id) ? (
				<div className="mt-24 mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
					{/* Banner Principal */}
					<div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
						{/* Imagen del Banner - Full width en mobile, lado izquierdo en desktop */}
						<div className="w-full">
							<div className="h-64 sm:h-80 lg:h-96 w-full overflow-hidden">
								<img
									className="h-full w-full object-cover"
									src={getAssetUrl(job.image)}
									alt="Banner principal de la oferta de trabajo"
								/>
							</div>
						</div>

						{/* Información del Trabajo */}
						<div className="p-6 sm:p-8 lg:p-12">
							{/* Título */}
							<h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-800 mb-6 lg:mb-8">
								{job.title}
							</h1>

							{/* Contenedor de info rápida - Grid responsivo */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
								{/* Columna 1: Prioridad y Fecha */}
								<div className="space-y-4">
									{/* Prioridad */}
									<div className="flex items-center gap-3">
										<span className="text-sm font-semibold text-gray-700">Prioridad:</span>
										<span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
											{job.priority}
										</span>
									</div>

									{/* Fecha de publicación */}
									<div className="flex items-center gap-2 text-sm text-gray-500">
										<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
										</svg>
										<span className="capitalize">
											Publicado: {moment(job.created).format("MMM D, YYYY")}
										</span>
									</div>
								</div>

								{/* Columna 2: Redes Sociales */}
								{(job?.profile?.facebook || job?.profile?.X || job?.profile?.instagram) && (
									<div className="space-y-3">
										<h3 className="text-sm font-semibold text-gray-700">Síguenos:</h3>
										<div className="flex items-center gap-3 flex-wrap">
											{job?.profile?.facebook && (
												<a 
													href={job.profile.facebook} 
													target="_blank" 
													rel="nofollow noreferrer"
													className="p-2.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all duration-200 hover:scale-110"
													aria-label="Facebook"
												>
													<svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-brand-facebook" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 10v4h3v7h4v-7h3l1 -4h-4v-2a1 1 0 0 1 1 -1h3v-4h-3a5 5 0 0 0 -5 5v2h-3" /></svg>
												</a>
											)}
											{job?.profile?.X && (
												<a 
													href={job.profile.X} 
													target="_blank" 
													rel="nofollow noreferrer"
													className="p-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-200 hover:scale-110"
													aria-label="X (Twitter)"
												>
													<svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-brand-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 4l11.733 16h4.267l-11.733 -16z" /><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" /></svg>
												</a>
											)}
											{job?.profile?.instagram && (
												<a 
													href={job.profile.instagram} 
													target="_blank" 
													rel="nofollow noreferrer"
													className="p-2.5 rounded-lg bg-pink-50 text-pink-600 hover:bg-pink-100 transition-all duration-200 hover:scale-110"
													aria-label="Instagram"
												>
													<svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-brand-instagram" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 4m0 4a4 4 0 0 1 4 -4h8a4 4 0 0 1 4 4v8a4 4 0 0 1 -4 4h-8a4 4 0 0 1 -4 -4z" /><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M16.5 7.5l0 .01" /></svg>
												</a>
											)}
										</div>
									</div>
								)}
							</div>

							{/* Información de la Empresa - Destacada */}
							<div className="mb-8 p-5 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 shadow-sm">
								<div className="flex items-start gap-4 sm:gap-5">
									<div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden shadow-md ring-4 ring-white">
										<img 
											src={getAssetUrl(job?.user?.picture)} 
											className="h-full w-full object-cover" 
											alt={`Logo de ${job?.user?.enterprise || "empresa"}`} 
										/>
									</div>
									<div className="flex-1 min-w-0">
										<h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
											{job?.user?.enterprise}
										</h2>
										{job?.profile?.address && (
											<p className="text-sm text-gray-600 flex items-start gap-2">
												<svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 h-5 w-5 mt-0.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
												</svg>
												<span className="break-words">{job?.profile?.address}</span>
											</p>
										)}
									</div>
								</div>
							</div>

							{/* Descripción */}
							<div className="border-t border-gray-100 pt-6">
								<h3 className="text-lg font-semibold text-gray-800 mb-3">Acerca de esta vacante</h3>
								<p className="text-base sm:text-lg text-gray-600 leading-relaxed">
									{clampHtmlToPlainText(job.description)}
								</p>
							</div>
						</div>
					</div>

					{/* Contenido Detallado */}
					<div className="mt-8 bg-white rounded-2xl shadow-lg overflow-hidden">
						<div className="px-6 py-8 sm:px-8 sm:py-10 lg:px-12">
							<div className="prose prose-lg max-w-none prose-indigo text-gray-600">
								<div
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(job.content),
									}}
								/>
							</div>
						</div>
					</div>

					{/* Formulario de Postulación */}
					{/* Formulario de Postulación */}
					<div className="mt-8">
						<div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
							<div className="px-6 py-8 sm:px-8 sm:py-10 lg:px-12">
								<div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
									{/* Información de la Vacante */}
									<div className="lg:col-span-5 lg:border-r lg:border-gray-100 lg:pr-10">
										<h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
											Postúlate a esta vacante
										</h2>
										<p className="text-gray-600 leading-7 mb-6">
											Completa tus datos y adjunta tu hoja de vida. Esta postulación se envía directamente a la empresa.
										</p>
										
										<div className="space-y-4">
											<div className="rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 p-5 border border-gray-200">
												<div className="space-y-3">
													<div className="flex items-start gap-3">
														<svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 h-5 w-5 text-gray-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
														</svg>
														<div className="flex-1 min-w-0">
															<p className="text-sm font-semibold text-gray-700">Vacante</p>
															<p className="text-sm text-gray-600 mt-0.5">{job.title}</p>
														</div>
													</div>
													
													<div className="flex items-start gap-3">
														<svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 h-5 w-5 text-gray-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
														</svg>
														<div className="flex-1 min-w-0">
															<p className="text-sm font-semibold text-gray-700">Empresa</p>
															<p className="text-sm text-gray-600 mt-0.5">{job?.user?.enterprise || "Empresa"}</p>
														</div>
													</div>
												</div>
											</div>

											<div className="rounded-xl bg-blue-50 p-5 border border-blue-100">
												<h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
													<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
													Requisitos
												</h3>
												<ul className="space-y-2 text-sm text-gray-700">
													<li className="flex items-start gap-2">
														<svg className="flex-shrink-0 h-5 w-5 text-green-600 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
															<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
														</svg>
														<span>CV en formato PDF, DOC o DOCX</span>
													</li>
													<li className="flex items-start gap-2">
														<svg className="flex-shrink-0 h-5 w-5 text-green-600 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
															<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
														</svg>
														<span>Límite: 1 postulación por computador al día</span>
													</li>
												</ul>
											</div>
										</div>
									</div>

									{/* Formulario */}
									<div className="lg:col-span-7">
										<div className="rounded-xl border border-green-100 bg-green-50/60 p-5 mb-6">
											<label className="flex items-start gap-3 cursor-pointer group">
												<input
													type="checkbox"
													checked={showApplyForm}
													onChange={(event) => {
														const checked = event.target.checked;
														setShowApplyForm(checked);
														if (!checked) {
															setApplyFeedback({ type: "", message: "" });
														}
													}}
													className="mt-0.5 h-5 w-5 rounded accent-green-corpo"
												/>
												<div className="flex-1">
													<span className="text-sm font-semibold text-gray-800 group-hover:text-green-corpo transition-colors">
														Quiero postularme a esta vacante
													</span>
													<p className="mt-1 text-xs text-gray-600">
														Marca esta opción para mostrar el formulario de postulación.
													</p>
													{applyLockedToday && (
														<p className="mt-2 text-xs font-medium text-green-corpo flex items-center gap-1">
															<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
															</svg>
															Ya se envió una postulación desde este computador hoy.
														</p>
													)}
												</div>
											</label>
										</div>

										{showApplyForm ? (
											<form onSubmit={handleApplySubmit} className="space-y-5">
												<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
													<div>
														<label htmlFor="full_name" className="block text-sm font-semibold text-gray-700 mb-2">
															Nombre completo *
														</label>
														<input
															id="full_name"
															name="full_name"
															type="text"
															required
															value={applicationForm.full_name}
															onChange={handleInputChange}
															placeholder="Ej: Juan Pérez"
															className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-transparent transition-shadow"
														/>
													</div>

													<div>
														<label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
															Correo electrónico *
														</label>
														<input
															id="email"
															name="email"
															type="email"
															required
															value={applicationForm.email}
															onChange={handleInputChange}
															placeholder="correo@ejemplo.com"
															className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-transparent transition-shadow"
														/>
													</div>
												</div>

												<div>
													<label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
														Teléfono (opcional)
													</label>
													<input
														id="phone"
														name="phone"
														type="text"
														inputMode="numeric"
														maxLength={15}
														value={applicationForm.phone}
														onChange={handleInputChange}
														placeholder="3001234567"
														className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-transparent transition-shadow"
													/>
												</div>

												<div>
													<label htmlFor="cv" className="block text-sm font-semibold text-gray-700 mb-2">
														Hoja de vida (PDF/Word) *
													</label>
													<input
														id="cv"
														name="cv"
														type="file"
														required
														accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
														onChange={handleCvChange}
														className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-green-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-green-corpo hover:file:bg-green-100 file:transition-colors focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-transparent"
													/>
													{applicationForm.cv && (
														<p className="mt-2 text-sm text-gray-600 flex items-center gap-2">
															<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
															</svg>
															<span className="truncate">{applicationForm.cv.name}</span>
														</p>
													)}
												</div>

												<div>
													<label htmlFor="cover_letter" className="block text-sm font-semibold text-gray-700 mb-2">
														Carta de presentación (opcional)
													</label>
													<textarea
														id="cover_letter"
														name="cover_letter"
														rows="5"
														value={applicationForm.cover_letter}
														onChange={handleInputChange}
														placeholder="Cuéntanos brevemente tu experiencia o por qué aplicas a esta vacante."
														className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-transparent transition-shadow resize-none"
													/>
												</div>

												{applyFeedback.message && (
													<div
														className={`rounded-lg px-5 py-4 text-sm font-medium ${
															applyFeedback.type === "success"
																? "bg-green-50 text-green-800 border-2 border-green-200"
																: "bg-red-50 text-red-800 border-2 border-red-200"
														}`}
													>
														<div className="flex items-start gap-3">
															{applyFeedback.type === "success" ? (
																<svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 h-5 w-5 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
																</svg>
															) : (
																<svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 h-5 w-5 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
																</svg>
															)}
															<span>{applyFeedback.message}</span>
														</div>
													</div>
												)}

												<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-100">
													<p className="text-xs text-gray-500 flex items-start gap-2">
														<svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 h-4 w-4 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
														</svg>
														<span>Al enviar, aceptas compartir tus datos con la empresa.</span>
													</p>
													<button
														type="submit"
														disabled={isApplying || applyLockedToday}
														className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-corpo px-6 py-3 text-sm font-semibold text-white shadow-md hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none transition-all duration-200"
													>
														{isApplying ? (
															<>
																<svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
																	<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
																	<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
																</svg>
																<span>Enviando...</span>
															</>
														) : applyLockedToday ? (
															"Postulación bloqueada hoy"
														) : (
															<>
																<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
																</svg>
																<span>Enviar postulación</span>
															</>
														)}
													</button>
												</div>
											</form>
										) : (
											<div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
												<svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												<p className="text-sm text-gray-600">
													El formulario de postulación se mostrará cuando marques la opción 
													<span className="block mt-1 font-semibold text-green-corpo">✓ Quiero postularme a esta vacante</span>
												</p>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<>Loading</>
			)}
			<Footer />
		</Layout>
	);
}

export default JobDetail;
